'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi } from '../lib/api';
import { setUserId, setUserProperties } from '../lib/analytics';

interface User {
  _id: string;
  email: string;
  name?: string;
  familyId?: string;
  emailVerified: boolean;
}

interface FamilyMemberSummary {
  userId: string;
  email: string;
  name?: string;
  role: 'admin' | 'member';
}

interface Family {
  _id: string;
  name: string;
  members: FamilyMemberSummary[];
}

interface AuthContextValue {
  user: User | null;
  family: Family | null;
  currentMember: FamilyMemberSummary | null;
  isAdmin: boolean;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, name: string, familyName?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [family, setFamily] = useState<Family | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshSession = useCallback(async () => {
    try {
      const session = await authApi.getSession();
      setUser(session.user);
      const fam = session.family ? (session.family as unknown as Family) : null;
      setFamily(fam);

      // Analytics: identify user
      setUserId(session.user._id);
      const member = fam?.members.find((m) => m.userId === session.user._id);
      setUserProperties({
        family_id: fam?._id,
        is_admin: member?.role === 'admin',
        member_count: fam?.members.length,
        email_verified: session.user.emailVerified,
        platform: 'web',
      });
    } catch {
      // Not authenticated or session expired
      setUser(null);
      setFamily(null);
    }
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    await authApi.login(email, password);
    await refreshSession();
  }, [refreshSession]);

  const signup = useCallback(async (email: string, password: string, name: string, familyName?: string) => {
    await authApi.signup(email, password, name, familyName);
    await refreshSession();
  }, [refreshSession]);

  const logout = useCallback(async () => {
    await authApi.logout();
    setUser(null);
    setFamily(null);
    setUserId(null);
  }, []);

  // Check auth status on mount
  useEffect(() => {
    refreshSession().finally(() => setIsLoading(false));
  }, [refreshSession]);

  const currentMember = (user && family)
    ? (family.members.find((m) => m.userId === user._id) ?? null)
    : null;

  const isAdmin = currentMember?.role === 'admin';

  const value: AuthContextValue = {
    user,
    family,
    currentMember,
    isAdmin,
    isLoading,
    isAuthenticated: !!user,
    login,
    signup,
    logout,
    refreshSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
